import { useState, useCallback, useRef } from 'react';
import { socketService } from '@/services/SocketService';
import { store } from '@/redux/common/store';
import { buildAgentContextMeta } from '@/redux/selectors/buildUIStateSnapshot';
import { useAudioCapture } from '@/hooks/useAudioCapture';
import { usePCMAudioStream } from '@/hooks/usePCMAudioStream';
import type { TextMessage } from '@/components/chat/MessageBubble';

export type VoiceMode = 'text' | 'ptt' | 'interactive';

function int16ToBase64(int16: Int16Array): string {
  const bytes = new Uint8Array(int16.buffer, int16.byteOffset, int16.byteLength);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

export function useVoiceMode(opts: {
  conversationId: string;
  assistantId: string;
  audioOut: ReturnType<typeof usePCMAudioStream>;
  setConversationId: (id: string) => void;
  setMessages: React.Dispatch<React.SetStateAction<{ kind: string; id: string; streaming?: boolean }[]>>;
  setLiveCaption: (text: string) => void;
  /** STT provider from ai_models config — sent in audio_end so the gateway routes correctly. */
  sttProvider?: string;
  /** Staged attachments to send with a push-to-talk turn (read at audio_end). */
  getPendingFiles?: () => { name: string; type: string; url: string }[];
  /** Called after a PTT turn's attachments have been sent, so the composer can clear them. */
  onFilesConsumed?: () => void;
  /** Selected microphone device id ('' = system default). Applied at capture start. */
  inputDeviceId?: string;
}): {
  mode: VoiceMode;
  recording: boolean;
  playbackRef: React.RefObject<boolean>;
  mediaRefId: React.RefObject<string>;
  // Push-to-talk recorder (hold→lock→swipe). startPtt on press; release → sendPtt (or cancelPtt
  // if too short); slide up → lockPtt; slide left → cancelPtt; pause/resume within a take.
  startPtt: () => Promise<void>;
  sendPtt: () => void;
  cancelPtt: () => void;
  pausePtt: () => void;
  resumePtt: () => Promise<void>;
  lockPtt: () => void;
  /** PTT recording is paused (capture stopped, buffer kept). */
  paused: boolean;
  /** PTT recording is locked (finger released → explicit delete/pause/send controls). */
  pttLocked: boolean;
  /** Elapsed PTT recording time in milliseconds. */
  recordMs: number;
  /** Live PTT input levels (0..1), oldest→newest, for the waveform. */
  pttLevels: number[];
  /** The paused PTT take is currently being played back. */
  pttPlaying: boolean;
  /** Play back the recorded (paused) PTT take through the speaker. */
  playPtt: () => void;
  /** Stop PTT playback. */
  stopPttPlayback: () => void;
  toggleInteractive: (extraMeta?: Record<string, unknown>) => Promise<void>;
  stopInteractive: () => void;
  /** Send a typed message straight to the live realtime agent while an interactive session is
   *  connected (chat_settings.interactive_text_input_enabled). No-op outside interactive mode. */
  sendRealtimeText: (text: string) => void;
  haltVoice: () => void;
  setMode: React.Dispatch<React.SetStateAction<VoiceMode>>;
  setRecording: React.Dispatch<React.SetStateAction<boolean>>;
  /** Mic muted — stops transmitting captured audio without ending the session. */
  micMuted: boolean;
  toggleMicMute: () => void;
  /** Speaker muted — silences SAM's voice playback without ending the session. */
  speakerMuted: boolean;
  toggleSpeakerMute: () => void;
  /** Re-acquire the mic (e.g. after the user picks a different input device mid-session). */
  restartCapture: () => Promise<void>;
} {
  const { conversationId, assistantId, audioOut, setConversationId, setMessages, setLiveCaption, sttProvider, getPendingFiles, onFilesConsumed, inputDeviceId } = opts;

  const [mode, setMode] = useState<VoiceMode>('text');
  const [recording, setRecording] = useState(false);
  const [micMuted, setMicMuted] = useState(false);
  const [speakerMuted, setSpeakerMuted] = useState(false);
  // Push-to-talk recorder state (hold→lock→swipe): locked = finger released, paused = capture
  // stopped but buffer kept, recordMs = elapsed time, pttLevels = live waveform amplitudes.
  const [paused, setPaused] = useState(false);
  const [pttLocked, setPttLocked] = useState(false);
  const [recordMs, setRecordMs] = useState(0);
  const [pttLevels, setPttLevels] = useState<number[]>([]);
  const [pttPlaying, setPttPlaying] = useState(false);
  // Raw captured PTT audio (24 kHz mono Int16), kept locally so a paused take can be played back
  // before sending. Cleared whenever a take starts/sends/cancels.
  const pttChunksRef = useRef<Int16Array[]>([]);
  const playbackCtxRef = useRef<AudioContext | null>(null);
  const playbackSrcRef = useRef<AudioBufferSourceNode | null>(null);
  const recStartRef = useRef(0);
  const recAccumRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  // Read inside onChunk without re-creating the capture callback on every toggle.
  const micMutedRef = useRef(false);
  // Paused PTT take: capture is stopped, but this guards onChunk too so any in-flight/buffered
  // chunk after stop() can't emit, drive the waveform, or get recorded — the wave freezes when paused.
  const pausedRef = useRef(false);
  // Latest selected mic device id, read at capture start (and on restart).
  const inputDeviceIdRef = useRef(inputDeviceId);
  inputDeviceIdRef.current = inputDeviceId;

  const playbackRef = useRef<boolean>(false);
  const mediaRefId = useRef<string>('');

  const ensureConversationId = useCallback(() => {
    if (!conversationId) {
      const id = crypto.randomUUID();
      setConversationId(id);
      return id;
    }
    return conversationId;
  }, [conversationId, setConversationId]);

  const onChunk = useCallback((int16: Int16Array) => {
    if (micMutedRef.current) return;  // muted — capture keeps running but we don't transmit
    if (pausedRef.current) return;    // paused PTT take — ignore any lingering chunk after stop()
    const audio = int16ToBase64(int16);
    if (mode === 'interactive') {
      socketService.emit('message', { type: 'agent_realtime.audio_chunk', session_id: conversationId, data: audio });
    } else {
      socketService.emit('message', {
        type: 'agent.audio_chunk',
        media_reference_id: mediaRefId.current,
        audio,
        assistant_id: assistantId,
        conversation_id: conversationId || undefined,
      });
      // Keep the raw samples for local playback of a paused take. Each chunk is a fresh transferred
      // buffer (see useAudioCapture), so holding the reference is safe.
      pttChunksRef.current.push(int16);
      // Drive the live waveform from this chunk's RMS level (0..1, amplified for visibility).
      let sum = 0;
      for (let i = 0; i < int16.length; i++) { const v = int16[i] / 32768; sum += v * v; }
      const level = Math.min(1, Math.sqrt(sum / int16.length) * 4);
      setPttLevels(prev => (prev.length >= 48 ? [...prev.slice(prev.length - 47), level] : [...prev, level]));
    }
  }, [mode, assistantId, conversationId]);

  const capture = useAudioCapture(onChunk, () => inputDeviceIdRef.current);

  // Re-acquire the mic with the currently-selected input device (used when the user picks a
  // different microphone during a live session — the socket session stays open).
  const restartCapture = useCallback(async () => {
    capture.stop();
    await capture.start();
  }, [capture]);

  const haltVoice = useCallback(() => {
    playbackRef.current = false;
    audioOut.stop();
    setLiveCaption('');
    setMessages(prev => prev.map(m => (m.kind === 'text' && m.streaming ? { ...m, streaming: false } : m)));
  }, [audioOut, setLiveCaption, setMessages]);

  const toggleMicMute = useCallback(() => {
    micMutedRef.current = !micMutedRef.current;
    setMicMuted(micMutedRef.current);
  }, []);

  const toggleSpeakerMute = useCallback(() => {
    setSpeakerMuted(prev => { const next = !prev; audioOut.setMuted(next); return next; });
  }, [audioOut]);

  // A fresh voice session always starts unmuted (mic + speaker on).
  const resetMutes = useCallback(() => {
    micMutedRef.current = false;
    setMicMuted(false);
    setSpeakerMuted(false);
    audioOut.setMuted(false);
  }, [audioOut]);

  // ── Push-to-talk recorder ────────────────────────────────────────────────────
  // Chunk streaming is unchanged: onChunk emits agent.audio_chunk live while capturing, the gateway
  // buffers them per media_reference_id, and `send` finalises with agent.audio_end (STT). `cancel`
  // emits agent.audio_cancel so the gateway discards the buffer and nothing is transcribed/sent.
  // Pause/resume just stop/restart capture against the SAME media_reference_id (same buffer).
  const startTimer = useCallback(() => {
    recStartRef.current = Date.now();
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => {
      setRecordMs(recAccumRef.current + (Date.now() - recStartRef.current));
    }, 200);
  }, []);
  const stopTimer = useCallback(() => {
    if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null; }
    if (recStartRef.current) recAccumRef.current += Date.now() - recStartRef.current;
    recStartRef.current = 0;
    setRecordMs(recAccumRef.current);
  }, []);
  // ── Paused-take playback ─────────────────────────────────────────────────────
  const stopPttPlayback = useCallback(() => {
    try { playbackSrcRef.current?.stop(); } catch { /* already stopped/ended */ }
    playbackSrcRef.current = null;
    playbackCtxRef.current?.close().catch(() => undefined);
    playbackCtxRef.current = null;
    setPttPlaying(false);
  }, []);

  const playPtt = useCallback(() => {
    const chunks = pttChunksRef.current;
    const total = chunks.reduce((n, c) => n + c.length, 0);
    if (!total) return;                       // nothing recorded yet
    stopPttPlayback();
    const ctx = new AudioContext();
    playbackCtxRef.current = ctx;
    const buffer = ctx.createBuffer(1, total, 24000);  // captured at 24 kHz mono
    const ch = buffer.getChannelData(0);
    let offset = 0;
    for (const c of chunks) {
      for (let i = 0; i < c.length; i++) ch[offset + i] = c[i] / 32768;
      offset += c.length;
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(ctx.destination);
    src.onended = () => setPttPlaying(false);
    playbackSrcRef.current = src;
    setPttPlaying(true);
    src.start();
  }, [stopPttPlayback]);

  const resetRecorder = useCallback(() => {
    if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null; }
    stopPttPlayback();
    pttChunksRef.current = [];
    pausedRef.current = false;
    recAccumRef.current = 0;
    recStartRef.current = 0;
    setRecordMs(0);
    setPttLevels([]);
    setPaused(false);
    setPttLocked(false);
  }, [stopPttPlayback]);

  const startPtt = useCallback(async () => {
    audioOut.stop();
    playbackRef.current = true;
    mediaRefId.current = crypto.randomUUID();
    resetRecorder();
    setMode('ptt');
    setRecording(true);
    startTimer();
    await capture.start();
  }, [audioOut, capture, resetRecorder, startTimer]);

  const sendPtt = useCallback(() => {
    capture.stop();
    setRecording(false);
    setLiveCaption('');
    const convId = ensureConversationId();
    const files = getPendingFiles?.() || [];
    socketService.emit('message', {
      type: 'agent.audio_end',
      media_reference_id: mediaRefId.current,
      assistant_id: assistantId,
      conversation_id: convId,
      interaction_mode: 'push_to_talk',
      stt_provider: sttProvider || 'openai',
      files,
      // timezone/location (user_context) + current page/mode/view (ui) for this turn.
      meta: buildAgentContextMeta(store.getState()),
    });
    if (files.length) onFilesConsumed?.();
    setMode('text');
    resetRecorder();
  }, [capture, assistantId, ensureConversationId, setLiveCaption, sttProvider, getPendingFiles, onFilesConsumed, resetRecorder]);

  const cancelPtt = useCallback(() => {
    capture.stop();
    setRecording(false);
    setLiveCaption('');
    socketService.emit('message', {
      type: 'agent.audio_cancel',
      media_reference_id: mediaRefId.current,
      conversation_id: conversationId || undefined,
    });
    setMode('text');
    resetRecorder();
  }, [capture, conversationId, setLiveCaption, resetRecorder]);

  const pausePtt = useCallback(() => {
    pausedRef.current = true;   // freeze the waveform immediately, before capture fully unwinds
    capture.stop();
    stopTimer();
    setPaused(true);
  }, [capture, stopTimer]);

  const resumePtt = useCallback(async () => {
    stopPttPlayback();          // stop any in-progress playback before recording resumes
    pausedRef.current = false;
    setPaused(false);
    startTimer();
    await capture.start();
  }, [capture, startTimer, stopPttPlayback]);

  const lockPtt = useCallback(() => { setPttLocked(true); }, []);

  const toggleInteractive = useCallback(async (extraMeta?: Record<string, unknown>) => {
    if (mode === 'interactive') {
      capture.stop();
      socketService.emit('message', { type: 'agent_realtime.session.end', session_id: conversationId });
      haltVoice();
      setMode('text');
      setRecording(false);
      resetMutes();
    } else {
      audioOut.stop();
      playbackRef.current = true;
      resetMutes();
      const sid = conversationId || crypto.randomUUID();
      setConversationId(sid);
      setMode('interactive');
      socketService.emit('message', {
        type: 'agent_realtime.session.start', session_id: sid, assistant_id: assistantId,
        // timezone/location (user_context) + current page/mode/view (ui) at session start.
        meta: buildAgentContextMeta(store.getState()),
        ...extraMeta,
      });
      await capture.start();
      setRecording(true);
    }
  }, [mode, capture, conversationId, assistantId, audioOut, haltVoice, resetMutes, setConversationId]);

  const stopInteractive = useCallback(() => {
    if (mode === 'interactive') {
      capture.stop();
      socketService.emit('message', { type: 'agent_realtime.session.end', session_id: conversationId });
    }
    haltVoice();
    setMode('text');
    setRecording(false);
    resetMutes();
  }, [mode, capture, conversationId, haltVoice, resetMutes]);

  const sendRealtimeText = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed || mode !== 'interactive') return;
    const convId = ensureConversationId();
    setMessages(prev => [...prev, {
      kind: 'text', id: `u-${Date.now()}`, role: 'user', text: trimmed, createdAt: Date.now(),
    } as TextMessage]);
    socketService.emit('message', { type: 'agent_realtime.text', session_id: convId, text: trimmed });
  }, [mode, ensureConversationId, setMessages]);

  return {
    mode,
    recording,
    playbackRef,
    mediaRefId,
    startPtt,
    sendPtt,
    cancelPtt,
    pausePtt,
    resumePtt,
    lockPtt,
    paused,
    pttLocked,
    recordMs,
    pttLevels,
    pttPlaying,
    playPtt,
    stopPttPlayback,
    toggleInteractive,
    stopInteractive,
    sendRealtimeText,
    haltVoice,
    setMode,
    setRecording,
    micMuted,
    toggleMicMute,
    speakerMuted,
    toggleSpeakerMute,
    restartCapture,
  };
}
